import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  Link,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  FormControlLabel,
  styled,
} from "@mui/material";
import _ from "lodash";
import { nanoid } from "nanoid";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import useSupplements from "../../hooks/useSupplements";
import StandardButton from "../StandardButton";
import useEvents from "../../hooks/useEvents";

// Styled components to match nutrition/cardio goals styling
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FormLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SupplementPrescriptionDialog = ({
  client,
  supplementPrescription,
  open,
  onDialogClosed,
}) => {
  const { supplements } = useSupplements();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const events = useEvents();

  // Validate that all required fields are filled out
  function validate() {
    if (!data.supplementId || data.supplementId.trim() === "") {
      setError("Please select a supplement");
      return false;
    }

    if (!data.dosage || data.dosage.trim() === "") {
      setError("Please enter a dosage amount");
      return false;
    }

    if (!data.frequency || data.frequency.trim() === "") {
      setError("Please enter a frequency");
      return false;
    }

    return true;
  }

  React.useEffect(() => {
    if (supplementPrescription) {
      setData(_.cloneDeep(supplementPrescription));
    } else {
      setData({
        id: nanoid(),
        supplementId: "",
        dosage: "",
        frequency: "",
        instructions: "",
        isActive: true,
        updatedOn: new Date(),
      });
    }
  }, [open, supplementPrescription]);

  const selectedSupplement = React.useMemo(() => {
    if (data && data.supplementId && data.supplementId.length > 0) {
      return supplements.find(
        (supplement) => supplement.id === data.supplementId,
      );
    }
    return null;
  }, [data, supplements]);

  if (!data || !open) {
    return null;
  }

  const isEditMode = !!supplementPrescription;

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => {
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <StyledDialogTitle>
        <LocalPharmacyIcon />
        {isEditMode
          ? "Edit Supplement Prescription"
          : "Add Supplement Prescription"}
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormSection>
              <FormLabel>Supplement Information</FormLabel>
              <Divider sx={{ mb: 2 }} />

              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel id="supplement-select-label">Supplement</InputLabel>
                <Select
                  labelId="supplement-select-label"
                  id="supplement-select"
                  value={data.supplementId}
                  label="Supplement"
                  onChange={(event) => {
                    setData({
                      ...data,
                      supplementId: event.target.value,
                    });
                  }}
                >
                  {supplements.map((supplement) => (
                    <MenuItem key={supplement.id} value={supplement.id}>
                      {supplement.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedSupplement && selectedSupplement.salesLink && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Purchase Link:
                  </Typography>
                  <Link
                    href={selectedSupplement.salesLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {selectedSupplement.salesLink}
                  </Link>
                </Box>
              )}
            </FormSection>
          </Grid>

          <Grid item xs={12}>
            <FormSection>
              <FormLabel>Prescription Details</FormLabel>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Dosage"
                    value={data.dosage}
                    variant="outlined"
                    placeholder="e.g., 1 capsule, 5g powder"
                    helperText="Amount to take each time"
                    onChange={(event) => {
                      setData({
                        ...data,
                        dosage: event.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Frequency"
                    value={data.frequency}
                    variant="outlined"
                    placeholder="e.g., 3x daily, Before workouts"
                    helperText="How often to take the supplement"
                    onChange={(event) => {
                      setData({
                        ...data,
                        frequency: event.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instructions"
                    value={data.instructions}
                    variant="outlined"
                    multiline
                    rows={4}
                    placeholder="Enter any special instructions for taking this supplement"
                    helperText="Additional details like timing with meals, etc."
                    onChange={(event) => {
                      setData({
                        ...data,
                        instructions: event.target.value,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={data.isActive}
                        onChange={() => {
                          setData({
                            ...data,
                            isActive: !data.isActive,
                          });
                        }}
                        color="primary"
                      />
                    }
                    label="Mark as active supplement"
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Grid>
        </Grid>

        {error && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "#FFEBEE", borderRadius: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button
          variant="outlined"
          onClick={() => {
            if (onDialogClosed) {
              onDialogClosed(null);
            }
          }}
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            if (validate()) {
              if (onDialogClosed) {
                onDialogClosed({
                  id: data.id,
                  supplementId: data.supplementId,
                  dosage: data.dosage,
                  frequency: data.frequency,
                  instructions: data.instructions,
                  isActive: data.isActive,
                  updatedOn: new Date(),
                });
                const logEvent = {
                  buttonName: isEditMode ? "Edit Supplement" : "Add Supplement",
                  pageName: "Clients",
                  tabName: client?.uid || "",
                };
                events.logButtonClick(logEvent);
              }
            }
          }}
        >
          {isEditMode ? "Update" : "Save"}
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default SupplementPrescriptionDialog;
